import React from 'react';
import { CheckCircle, Target, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

export const About = ({ onQuoteClick }) => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              About Ekaanshi Indoor
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Crafting premium doors and windows that blend modern design with
              exceptional functionality
            </p>
          </div>
        </div>
      </section>

      {/* Company Introduction */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Who We Are
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Ekaanshi Indoor - Interiors & Décor Solutions is a leading
                provider of premium System Aluminium and uPVC doors and windows.
                With over 15 years of industry experience, we have established
                ourselves as a trusted partner for residential and commercial
                projects across the region.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Our expertise lies in delivering innovative, energy-efficient,
                and aesthetically pleasing solutions that transform ordinary
                spaces into extraordinary environments. We combine cutting-edge
                technology with traditional craftsmanship to create products that
                stand the test of time.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                From consultation to installation and beyond, we are committed to
                providing exceptional service and products that exceed our
                clients' expectations.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1533779283484-8ad4940aa3a8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwzfHxuYXR1cmFsJTIwbGlnaHR8ZW58MHx8fHwxNzY5MTAzMzY5fDA&ixlib=rb-4.1.0&q=85"
                alt="Modern Interior"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="bg-white p-10 rounded-2xl shadow-md">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6">
                <Target className="text-blue-900" size={32} />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Our Mission
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                To provide superior quality System Aluminium and uPVC solutions
                that enhance the comfort, energy efficiency, and aesthetic appeal
                of every space we touch. We strive to be the first choice for
                architects, builders, and homeowners seeking excellence in doors
                and windows.
              </p>
            </div>
            <div className="bg-white p-10 rounded-2xl shadow-md">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6">
                <Eye className="text-blue-900" size={32} />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Our Vision
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                To be recognized as the leading innovator in architectural
                solutions, setting new standards for quality, design, and
                customer satisfaction. We envision a future where every building
                benefits from our commitment to excellence and sustainability.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Experience & Expertise */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Expertise
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Decades of experience in System Aluminium & uPVC solutions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-6">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-4">
                <span className="text-4xl font-bold text-blue-900">15+</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Years Experience
              </h3>
              <p className="text-gray-600">
                Over a decade of industry leadership
              </p>
            </div>
            <div className="text-center p-6">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-4">
                <span className="text-4xl font-bold text-blue-900">500+</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Projects Completed
              </h3>
              <p className="text-gray-600">
                Successful installations across sectors
              </p>
            </div>
            <div className="text-center p-6">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-4">
                <span className="text-4xl font-bold text-blue-900">98%</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Client Satisfaction
              </h3>
              <p className="text-gray-600">
                Consistently exceeding expectations
              </p>
            </div>
            <div className="text-center p-6">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-4">
                <span className="text-4xl font-bold text-blue-900">50+</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Expert Team
              </h3>
              <p className="text-gray-600">
                Skilled professionals at your service
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Commitment */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Commitment
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Quality, precision, and customer satisfaction in every project
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-md">
              <CheckCircle className="text-blue-900 mb-4" size={40} />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Quality Assurance
              </h3>
              <p className="text-gray-600 leading-relaxed">
                We source materials only from certified manufacturers and conduct
                rigorous quality checks at every stage to ensure our products
                meet the highest industry standards.
              </p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-md">
              <CheckCircle className="text-blue-900 mb-4" size={40} />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Precision Engineering
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Our state-of-the-art manufacturing facility and skilled
                technicians ensure precise measurements, perfect fits, and
                flawless installation every time.
              </p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-md">
              <CheckCircle className="text-blue-900 mb-4" size={40} />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Customer First
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Your satisfaction is our priority. From initial consultation to
                after-sales support, we're committed to delivering exceptional
                service and building lasting relationships.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Partner With Us for Your Next Project
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Experience the Ekaanshi Indoor difference - where quality meets
            innovation
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={onQuoteClick}
              size="lg"
              className="bg-white text-blue-900 hover:bg-gray-100 text-lg px-8 py-6"
            >
              Request a Quote
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-blue-900 text-lg px-8 py-6"
            >
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;